<template>
  <div
    class="composer"
    :class="{ 'composer--drop-active': isDragOver }"
    @dragenter="onDragEnter"
    @dragleave="onDragLeave"
    @dragover="onDragOver"
    @drop="onDrop"
  >
    <transition name="drop-overlay-fade">
      <div v-if="isDragOver" class="drop-overlay">
        <v-icon size="40" color="primary">mdi-cloud-upload-outline</v-icon>
        <div class="drop-overlay__text">Drop files to attach</div>
      </div>
    </transition>
    <div v-if="localAttachments.length > 0" class="composer__top">
      <v-chip
        v-for="attachment in localAttachments"
        :key="attachment.id"
        size="small"
        closable
        :title="attachment.name"
        :disabled="$store.state.isWorkflowDisabled || attachment.uploading"
        @click:close="removeAttachment(attachment.id)"
      >
        <template #prepend>
          <v-progress-circular
            v-if="attachment.uploading"
            indeterminate
            :size="14"
            :width="2"
            class="mr-2"
          />
          <v-icon v-else :icon="attachmentIcon(attachment)" start></v-icon>
        </template>
        <span class="chip-name">{{ attachment.name }}</span>
      </v-chip>
    </div>
    <v-textarea
      class="composer__textarea"
      v-model="textModel"
      :placeholder="label"
      @update:model-value="changeDescription"
      @focus="onTextareaFocus"
      @blur="onTextareaBlur"
      @paste="onPaste"
      variant="solo"
      flat
      rows="3"
      auto-grow
      max-height="300"
      :autofocus="true"
      ref="textarea"
      :disabled="$store.state.isWorkflowDisabled"
    ></v-textarea>
    <div v-if="hint && !textModelData" class="composer__guidance">{{ hint }}</div>
    <div v-if="!textModelData" class="composer__suggestions" data-test="ai-description-examples">
      <span class="composer__hint">Try:</span>
      <v-chip
        v-for="example in suggestedExamples"
        :key="example"
        size="small"
        variant="outlined"
        :disabled="isDisabled"
        @click="populateTextarea(example)"
      >
        {{ example }}
      </v-chip>
      <v-menu content-class="example-options-content">
        <template #activator="{ props }">
          <v-chip
            v-bind="props"
            size="small"
            variant="text"
            color="primary"
            append-icon="mdi-chevron-down"
            :disabled="isDisabled"
          >
            More examples
          </v-chip>
        </template>
        <v-list>
          <v-list-item v-for="example in remainingExamples" :key="example" @click="populateTextarea(example)">
            <v-list-item-title>{{ example }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
    <div class="composer__bottom">
      <v-btn
        icon="mdi-paperclip"
        variant="text"
        size="small"
        density="comfortable"
        :disabled="$store.state.isWorkflowDisabled"
        @click.stop="triggerFileInput"
      >
        <v-icon>mdi-paperclip</v-icon>
        <v-tooltip activator="parent" location="top" open-delay="400">
          Attach file or drop here
        </v-tooltip>
      </v-btn>
      <span v-if="saving" class="composer__hint">
        <v-progress-circular indeterminate :size="12" :width="2" class="mr-1" />
        Saving...
      </span>
      <span v-else class="composer__hint">Supports {{ acceptedExtensionsLabel }}</span>
      <input
        ref="fileInput"
        type="file"
        :accept="fileInputAccept"
        style="display: none"
        @change="onFileSelected"
      />
    </div>
  </div>
</template>

<script>
import { workflowDescriptionExamples } from '@/ai/prompts/common/examples'
import randomUUID from '@/utils/uuid'
import { uploadAiAttachment, deleteAiAttachment } from '@/api/aiAttachmentsApi'
import {
  MAX_FILE_SIZE,
  MAX_TOTAL_SIZE,
  MAX_FILE_COUNT,
  acceptedMimeTypesFor,
  acceptedExtensionsLabelFor,
  fileInputAcceptFor,
  resolveMimeType,
  renameGenericFile
} from '@/utils/aiAttachments'

const SUGGESTED_EXAMPLE_COUNT = 2

export default {
  name: 'ExampleTextarea',
  props: {
    label: String,
    hint: String,
    saving: Boolean,
    text: String,
    projectId: String,
    workflowId: String,
    attachments: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    textModel: {
      get () {
        return this.textModelData
      },
      set (value) {
        this.textModelData = value
      },
      cache: false
    },
    suggestedExamples () {
      return workflowDescriptionExamples.slice(0, SUGGESTED_EXAMPLE_COUNT)
    },
    remainingExamples () {
      return workflowDescriptionExamples.slice(SUGGESTED_EXAMPLE_COUNT)
    },
    isDragOver () {
      return this.dragDepth > 0
    },
    isDisabled () {
      return this.$store.state.isWorkflowDisabled || this.saving
    },
    aiVersion () {
      return this.$store.state.workflow.workflow?.aiVersion
    },
    acceptedMimeTypes () {
      return acceptedMimeTypesFor(this.aiVersion)
    },
    acceptedExtensionsLabel () {
      return acceptedExtensionsLabelFor(this.aiVersion)
    },
    fileInputAccept () {
      return fileInputAcceptFor(this.aiVersion)
    }
  },
  data () {
    return {
      textModelData: this.text ?? '',
      localAttachments: [],
      dragDepth: 0,
      isTextareaFocused: false
      // rules: [v => !!v || 'Required.']
    }
  },
  methods: {
    onTextareaFocus () {
      this.isTextareaFocused = true
    },

    onTextareaBlur () {
      this.isTextareaFocused = false
    },

    populateTextarea (example) {
      this.textModel = example
      this.$emit('change-description', { description: example, skipDebounce: true })
      this.focusTextarea()
    },

    changeDescription () {
      this.$emit('change-description', { description: this.textModel })
    },

    triggerFileInput () {
      this.$refs.fileInput.value = ''
      this.$refs.fileInput.click()
    },

    onFileSelected (event) {
      this.handleFile(event.target.files?.[0])
    },

    async handleFile (file) {
      if (!file) {
        return
      }

      const mimeType = resolveMimeType(file)
      if (!this.acceptedMimeTypes.includes(mimeType)) {
        this.showAttachmentError(`Unsupported file type: ${file.name}. Allowed: ${this.acceptedExtensionsLabel}.`)
        return
      }

      if (this.localAttachments.length >= MAX_FILE_COUNT) {
        this.showAttachmentError(`Maximum ${MAX_FILE_COUNT} attachments allowed.`)
        return
      }

      if (file.size > MAX_FILE_SIZE) {
        this.showAttachmentError(`${file.name} is too large. Maximum file size is ${MAX_FILE_SIZE / (1024 * 1024)} MB.`)
        return
      }

      const totalSize = this.localAttachments.reduce((sum, attachment) => sum + attachment.size, 0) + file.size
      if (totalSize > MAX_TOTAL_SIZE) {
        this.showAttachmentError(`Total attachment size exceeds ${MAX_TOTAL_SIZE / (1024 * 1024)} MB.`)
        return
      }

      const tempId = randomUUID()
      this.localAttachments.push({
        id: tempId,
        name: file.name,
        size: file.size,
        mimeType,
        s3Key: null,
        uploading: true
      })

      try {
        const uploaded = await uploadAiAttachment({
          projectId: this.projectId,
          workflowId: this.workflowId,
          file,
          mimeType
        })
        const index = this.localAttachments.findIndex((attachment) => attachment.id === tempId)
        if (index === -1) {
          return
        }
        this.localAttachments.splice(index, 1, {
          ...this.localAttachments[index],
          s3Key: uploaded.s3Key,
          uploading: false
        })
        this.emitAttachments()
      } catch (err) {
        this.localAttachments = this.localAttachments.filter((attachment) => attachment.id !== tempId)
        this.showAttachmentError(`Failed to upload ${file.name}`)
      }
    },

    isDraggingFiles (event) {
      const types = event.dataTransfer?.types
      if (!types) {
        return false
      }

      return Array.from(types).includes('Files')
    },

    onDragEnter (event) {
      if (!this.isDraggingFiles(event)) {
        return
      }
      this.dragDepth++
    },

    onDragLeave (event) {
      if (!this.isDraggingFiles(event)) {
        return
      }
      this.dragDepth = Math.max(0, this.dragDepth - 1)
    },

    onDragOver (event) {
      if (!this.isDraggingFiles(event)) {
        return
      }
      event.preventDefault()
    },

    onDrop (event) {
      event.preventDefault()
      this.dragDepth = 0
      const files = Array.from(event.dataTransfer?.files || [])
      for (const file of files) {
        this.handleFile(file)
      }
    },

    onPaste (event) {
      const items = event.clipboardData?.items || []
      const pastedFiles = []
      for (const item of items) {
        if (item.kind === 'file') {
          const file = item.getAsFile()
          if (file) {
            pastedFiles.push(file)
          }
        }
      }
      if (pastedFiles.length === 0) {
        return
      }

      event.preventDefault()
      const reservedNames = this.localAttachments.map((attachment) => attachment.name)
      for (const file of pastedFiles) {
        const renamed = renameGenericFile(file, reservedNames)
        reservedNames.push(renamed.name)
        this.handleFile(renamed)
      }
    },

    showAttachmentError (message) {
      this.$store.commit('snackbar/showMessage', {
        content: message,
        timeout: 5000,
        color: 'red',
        location: 'center center'
      }, { root: true })
    },

    async removeAttachment (id) {
      const target = this.localAttachments.find((attachment) => attachment.id === id)
      if (!target) {
        return
      }

      const previousAttachments = [...this.localAttachments]
      this.localAttachments = this.localAttachments.filter((attachment) => attachment.id !== id)

      if (target.s3Key) {
        try {
          await deleteAiAttachment(target.s3Key)
        } catch (err) {
          this.localAttachments = previousAttachments
          this.showAttachmentError(`Failed to remove ${target.name}. Please try again.`)
          console.warn(`Failed to delete attachment from S3: ${target.s3Key}`, err)
          return
        }
      }

      this.emitAttachments()
    },

    emitAttachments () {
      const completed = this.localAttachments.filter((attachment) => !attachment.uploading)
      this.$emit('change-attachments', completed)
    },

    attachmentIcon (attachment) {
      if (attachment.mimeType?.startsWith('image/')) {
        return 'mdi-image-outline'
      }
      if (attachment.mimeType === 'application/pdf') {
        return 'mdi-file-pdf-box'
      }

      return 'mdi-file-document-outline'
    },

    focusTextarea () {
      this.$refs.textarea?.focus?.()
    },

    seedFromAttachmentsProp (source) {
      const inFlight = this.localAttachments.filter((attachment) => attachment.uploading)
      const settled = (source || []).map((attachment) => ({
        id: attachment.s3Key,
        name: attachment.name,
        size: attachment.size,
        mimeType: attachment.mimeType,
        s3Key: attachment.s3Key,
        uploading: false
      }))
      this.localAttachments = [...settled, ...inFlight]
    }
  },
  watch: {
    attachments (newAttachments) {
      this.seedFromAttachmentsProp(newAttachments)
    },
    text (rawText) {
      const newText = rawText ?? ''
      // Local edits win while the user is in the field: an in-flight save echoes back the
      // value it was sent with, which is already stale by the next keystroke.
      if (this.isTextareaFocused || newText === this.textModelData) {
        return
      }
      this.textModelData = newText
    }
  },
  mounted () {
    this.textModel = this.text ?? ''
    this.seedFromAttachmentsProp(this.attachments)
  }
}
</script>
<style scoped>

.composer {
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 8px 8px 4px 8px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.18);
  border-radius: 12px;
  background-color: rgb(var(--v-theme-surface));
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.composer:hover {
  border-color: rgba(var(--v-theme-on-surface), 0.32);
}

.composer:focus-within {
  border-color: rgba(var(--v-theme-primary), 0.7);
  box-shadow: 0 0 0 1px rgba(var(--v-theme-primary), 0.7);
}

.composer--drop-active,
.composer--drop-active:focus-within {
  border-style: dashed;
  border-color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.04);
  box-shadow: none;
}

.composer__top {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 4px 0 4px;
}

.chip-name {
  display: inline-block;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.composer__textarea :deep(.v-field--variant-solo) {
  background-color: transparent !important;
  box-shadow: none !important;
}

.composer__textarea :deep(.v-input__details) {
  display: none;
}

.composer__guidance {
  padding: 0 4px 6px 4px;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.composer__suggestions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 0 4px 8px 4px;
}

.composer__bottom {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 0 4px;
}

.composer__hint {
  font-size: 11px;
  color: rgba(var(--v-theme-on-surface), 0.5);
}

.drop-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background-color: rgba(var(--v-theme-surface), 0.94);
  border: 2px dashed rgb(var(--v-theme-primary));
  border-radius: 10px;
  pointer-events: none;
  z-index: 10;
}

.drop-overlay__text {
  font-size: 14px;
  font-weight: 500;
  color: rgb(var(--v-theme-primary));
}

.drop-overlay-fade-enter-active,
.drop-overlay-fade-leave-active {
  transition: opacity 0.12s ease;
}

.drop-overlay-fade-enter-from,
.drop-overlay-fade-leave-to {
  opacity: 0;
}

.example-options-content {
  max-height: 250px !important;
}
</style>
